<script>
  import { onMount } from 'svelte';
  import { requireLogin, setupSessionWatcher } from './components/lib/auth.js';

  import Navbar from './components/Navbar.svelte';
  import Footer from './components/Footer.svelte';
  import Sidebar from './components/Sidebar.svelte';

  import Dashboard from './pages/Dashboard.svelte';
  import Order from './pages/Order.svelte';
  import Database from './pages/Database.svelte';
  import OperatorPage from './pages/OperatorPage.svelte';
  import Download from './pages/Download.svelte';
  import MachinePhoto from './pages/MachinePhoto.svelte';
  import Login from './pages/Login.svelte';
  //import Waiting from './pages/Waiting.svelte';
  import AccountManage from './pages/AccountManage.svelte';

  let currentPage = 'dashboard';
  let sidebarOpen = true;
  let loggedInUser = null;
  let deviceId = null;
  let pollingStopper;

  const pages = {
    dashboard: Dashboard,
    order: Order,
    operatorpage: OperatorPage,
    database: Database,
    download: Download,
    machinephoto: MachinePhoto,
    login: Login,
    //waiting: Waiting,
    accountmanage: AccountManage      //관리자 전용
  };

  const fullscreenPages = ['operatorpage', 'login'];
  $: CurrentPage = pages[currentPage];
  $: isFullscreen = fullscreenPages.includes(currentPage);
  $: allowAccess =
      // 로그인된 사용자
      loggedInUser !== null ||
      // operatorpage+유효 device
      (currentPage === 'operatorpage' && Number.isInteger(deviceId) && deviceId >= 1 && deviceId <= 10) ||
      // 로그인 페이지
      currentPage === 'login';  // || currentPage === 'waiting';

  // pathWithQuery: "operatorpage?device=4", "machinephoto?device=05", "dashboard" 등
  function navigate(pathWithQuery) {
    const [pagePart, queryPart] = pathWithQuery.split('?');
    if (!pages[pagePart]) return;
    window.location.hash = `#/${pathWithQuery}`;
    // 해시 변경 직후 파싱
    parseHash();
  }

  function handleResize() {
    sidebarOpen = window.innerWidth >= 768;
  }

  function startServerSessionPolling() {
    const interval = setInterval(async () => {
      try {
        const res = await fetch('/api/users/check-session', { credentials: 'include' });
        const data = await res.json();
        if (!data.loggedIn) {
          alert('서버 세션이 만료되었습니다. 다시 로그인해주세요.');
          localStorage.removeItem('loggedInUser');
          clearInterval(interval);
          location.reload();
        }
      } catch (err) {
        console.error(err);
      }
    }, 60_000);
    return () => clearInterval(interval);
  }

  function stopServerSessionPolling() {
    if (pollingStopper) {
      pollingStopper();
      pollingStopper = null;
    }
  }

  // 해시 파싱 공통 함수
  function parseHash() {
    const raw = window.location.hash.replace('#/', '');
    const [pagePart, queryPart] = raw.split('?');
    const pageKey = pagePart.toLowerCase();
    const params = new URLSearchParams(queryPart || '');

    // deviceId 세팅
    const d = params.get('device');
    deviceId = d ? Number(d) : null;

    // 페이지 접근 제어
    const isOp = pageKey === 'operatorpage';
    const validOp = Number.isInteger(deviceId) && deviceId >= 1 && deviceId <= 10;
    const isLogin = pageKey === 'login';

    let serverLoggedIn = false;
    // 세션 확인
    return fetch('/api/users/check-session', { credentials: 'include' })
      .then(res => res.json())
      .then(data => {
        serverLoggedIn = data.loggedIn;
        if (!serverLoggedIn) localStorage.removeItem('loggedInUser');
      })
      .catch(() => {
        localStorage.removeItem('loggedInUser');
      })
      .finally(() => {
        // 접근 권한 없으면 로그인으로 리디렉션
        if (!serverLoggedIn && !isLogin && !(isOp && validOp)) {
          currentPage = 'login';
          window.location.hash = '#/login';
        } else if (pages[pageKey]) {
          currentPage = pageKey;
        }
      });
  }

  onMount(() => {
    const teardownWatcher = setupSessionWatcher();
    handleResize();

    // 최초 해시 처리
    parseHash().then(() => {
      // 자동 로그인 폴링
      if (currentPage !== 'operatorpage' || !(Number.isInteger(deviceId) && deviceId >= 1 && deviceId <= 10)) {
        loggedInUser = requireLogin();
        if (loggedInUser) {
          pollingStopper = startServerSessionPolling();
        }
      }
    });

    window.addEventListener('resize', handleResize);
    window.addEventListener('hashchange', parseHash);

    return () => {
      teardownWatcher();
      stopServerSessionPolling();
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('hashchange', parseHash);
    };
  });
</script>

{#if allowAccess}
  <div class="layout">
    {#if !isFullscreen}
      <Navbar on:navigate={e => navigate(e.detail)} />
      <Sidebar bind:isOpen={sidebarOpen} on:navigate={e => navigate(e.detail)} />
    {/if}

    <main
      class="main-content {sidebarOpen ? 'sidebar-open' : 'sidebar-closed'} {isFullscreen ? 'fullscreen' : ''}"
    >
      {#if currentPage === 'machinephoto'}
        <MachinePhoto deviceId={deviceId} />
      {:else}
        <svelte:component this={CurrentPage} />
      {/if}
    </main>

    {#if !isFullscreen}
      <Footer />
    {/if}
  </div>
{:else}
  <main class="main-content fullscreen">
    <Login />
  </main>
{/if}





<style>
  .layout {
    display: flex;
    flex-direction: column;
    min-height: 100vh;
    background-color: var(--pico-background-color);
    color: var(--pico-text-color);
  }

  .main-content {
    flex: 1;
    transition: margin-left 0.3s ease;
    padding-top: 60px;
    padding-bottom: 40px;
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
  }

  .main-content.sidebar-open {
    margin-left: 220px;
  }

  .main-content.sidebar-closed {
    margin-left: 60px;
  }

  .main-content.fullscreen {
    margin: 0;
    padding: 0;
    width: 100%;
    height: 100vh;
  }

  @media (max-width: 768px) {
    .main-content {
      margin-left: 70px !important;
    }
  }
</style>